import styles from './FolderSelect.module.scss';
import { useEffect, useState } from 'react';
import { FoldersOptions } from './FoldersOptions/FoldersOptions';
import { t } from 'i18next';
import ArrowIcon from '~/assets/svg/newSvg/arrow-select.svg?react';
import classNames from 'classnames';
import cn from 'classnames';

interface IFolderProps {
  id: number;
  name: string;
  child_folders: IFolderProps[];
}

interface IFolderSelectProps {
  folders: IFolderProps[];
  assetId: number;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  folderId?: number;
  onChange: (value: { id: number; name: string }) => void;
  disabled?: boolean;
}

export const FolderSelect = ({
  folders,
  folderId,
  assetId,
  label,
  placeholder,
  required,
  disabled,
  error,
  onChange,
}: IFolderSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [folderPath, setFolderPath] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(folderId || null);

  const findFolderPath = (
    folders: IFolderProps[],
    targetId: number,
    currentPath: string[] = []
  ): string[] | null => {
    for (const folder of folders) {
      const newPath = [...currentPath, folder.name];

      if (folder.id === targetId) {
        return newPath;
      }

      if (folder.child_folders.length > 0) {
        const result = findFolderPath(folder.child_folders, targetId, newPath);
        if (result) {
          return result;
        }
      }
    }
    return null;
  };

  const handleToggleSelect = () => {
    setIsOpen(!isOpen);
  };

  const handleClick = (id: number, name: string) => {
    setSelectedId(id);
    const fullPath = findFolderPath(folders, id);
    setFolderPath(fullPath || []);
    setIsOpen(false);
    onChange({ id, name });
  };

  const handleCloseDropdown = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (assetId) {
      const fullPath = findFolderPath(folders, assetId);
      setFolderPath(fullPath || []);
    }
  }, [assetId]);

  return (
    <div className={styles.folderSelectWrapper}>
      {label && (
        <span className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </span>
      )}
      <div
        className={cn(styles.folderSelect, disabled && styles.disabled)}
        onClick={disabled ? undefined : handleToggleSelect}
        onBlur={event => {
          handleCloseDropdown(event);
        }}
      >
        <div className={styles.folderSelectLabel}>
          {!folderPath.length ? (
            <span className={styles.placeholder}>
              {placeholder || t('folder_placeholder')}
            </span>
          ) : (
            <span className={classNames(styles.folderPath)}>
              {folderPath.join(' / ')}
            </span>
          )}
          <ArrowIcon
            className={cn(
              styles.arrowIcon,
              isOpen ? styles.arrowIconOpen : undefined
            )}
          />
        </div>

        {isOpen && (
          <FoldersOptions
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            folders={folders}
            nestQuantity={1}
            handleClick={handleClick}
          />
        )}
      </div>
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
};
